import PropTypes from 'prop-types';
import './Form.css';

/**
 * Inline helper / error text for a form control.
 *
 * Rendered as a `<div>` (not `<p>`) so that consumers may legally embed
 * inline-block children (icons, badges, `<em>`, etc.) without producing
 * invalid HTML. `role="paragraph"` keeps the semantic equivalence to a
 * paragraph for assistive technology.
 *
 * When `kind="err"`, `role="alert"` is applied so screen readers announce
 * the message when it appears (validation feedback).
 *
 * The `id` prop is required-but-optional: callers that wire
 * `aria-describedby` from the input must pass an `id`. `InputRow`
 * auto-injects a generated id when this is rendered via its `helper` slot.
 *
 * @param {object} props
 * @param {string} [props.id]  Used as the target of `aria-describedby`.
 * @param {'default'|'err'} [props.kind='default']
 *   `'err'` switches color to `--err` and sets `role="alert"` so screen
 *   readers announce the message when it appears.
 * @param {React.ReactNode} props.children
 *
 * @example
 *   <InputRow
 *     label="RUT"
 *     error
 *     helper={<HelperText kind="err">Formato inválido.</HelperText>}
 *   >
 *     <Input variant="mono" defaultValue="bad-rut" />
 *   </InputRow>
 */
export function HelperText({ id, kind = 'default', children }) {
  const isErr = kind === 'err';
  return (
    <div
      id={id}
      role={isErr ? 'alert' : 'paragraph'}
      className={`helper-text${isErr ? ' err' : ''}`}
    >
      {children}
    </div>
  );
}

HelperText.propTypes = {
  id: PropTypes.string,
  kind: PropTypes.oneOf(['default', 'err']),
  children: PropTypes.node,
};

export default HelperText;
